import {
  BarChart,
  Bar,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  CartesianGrid,
} from "recharts";

/** JSD 거리 분포 히스토그램 데이터 (시연용 프리컴퓨팅) */
const jsdDistribution = [
  { range: "0-0.1", count: 35, fill: "#22c55e" },
  { range: "0.1-0.2", count: 42, fill: "#22c55e" },
  { range: "0.2-0.3", count: 28, fill: "#84cc16" },
  { range: "0.3-0.4", count: 18, fill: "#eab308" },
  { range: "0.4-0.5", count: 10, fill: "#f97316" },
  { range: "0.5-0.6", count: 5, fill: "#ef4444" },
  { range: "0.6-0.7", count: 3, fill: "#ef4444" },
  { range: "0.7-0.83", count: 1, fill: "#dc2626" },
];

/** VP 범위 데이터 (시연용) */
const vpDistribution = [
  { logPa: -2, density: 2 },
  { logPa: -1, density: 5 },
  { logPa: 0, density: 12 },
  { logPa: 1, density: 25 },
  { logPa: 2, density: 38 },
  { logPa: 3, density: 30 },
  { logPa: 4, density: 18 },
  { logPa: 5, density: 8 },
  { logPa: 6, density: 3 },
];

/** Solubility 범위 데이터 (시연용) */
const solDistribution = [
  { logS: -8, density: 3 },
  { logS: -7, density: 5 },
  { logS: -6, density: 10 },
  { logS: -5, density: 18 },
  { logS: -4, density: 28 },
  { logS: -3, density: 35 },
  { logS: -2, density: 25 },
  { logS: -1, density: 15 },
  { logS: 0, density: 8 },
  { logS: 1, density: 4 },
  { logS: 2, density: 2 },
];

export default function PerformanceCharts() {
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
        <div className="w-2 h-2 bg-green-500 rounded-full" />
        성능 분포
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
        {/* JSD Distance Distribution */}
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-2">
            JSD 거리 분포
          </h4>
          <p className="text-xs text-gray-500 mb-3">
            0=완벽 매칭 / 0.833=최대 거리
          </p>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={jsdDistribution}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="range"
                tick={{ fontSize: 10 }}
                angle={-30}
                textAnchor="end"
                height={50}
              />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip
                formatter={(value) => [`${value}건`, "빈도"]}
              />
              <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                {jsdDistribution.map((entry) => (
                  <rect key={entry.range} fill={entry.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* VP Range */}
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-2">
            증기압 (VP) 예측 범위
          </h4>
          <p className="text-xs text-gray-500 mb-3">
            log Pa 단위 / 임계선: 2.0(중간), 3.5(고휘발)
          </p>
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={vpDistribution}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="logPa"
                tick={{ fontSize: 11 }}
                label={{
                  value: "log Pa",
                  position: "insideBottomRight",
                  offset: -5,
                  fontSize: 11,
                }}
              />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip
                formatter={(value) => [`${value}`, "밀도"]}
                labelFormatter={(label) => `log Pa: ${label}`}
              />
              <ReferenceLine
                x={2}
                stroke="#f97316"
                strokeDasharray="4 4"
                label={{ value: "중간", fill: "#f97316", fontSize: 10 }}
              />
              <ReferenceLine
                x={3.5}
                stroke="#ef4444"
                strokeDasharray="4 4"
              />
              <Area
                type="monotone"
                dataKey="density"
                stroke="#8b5cf6"
                fill="#c4b5fd"
                fillOpacity={0.6}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* RoMSH Placeholder */}
        <div
          data-testid="romsh-placeholder-card"
          className="bg-gray-50 border border-gray-200 rounded-lg p-6 flex flex-col items-center justify-center min-h-[300px]"
        >
          <span className="text-4xl mb-3" role="img" aria-label="대기 중">
            ⏳
          </span>
          <h3 className="text-sm font-semibold text-gray-700 mb-2">
            RoMSH 성능 (대기 중)
          </h3>
          <p className="text-xs text-gray-500 text-center leading-relaxed">
            캘리브레이션 데이터 협업 진행 중
            <br />
            인하대 results.json 수령 후 AUROC/F1 등 노출 예정
          </p>
        </div>

        {/* Solubility Range */}
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-2">
            용해도 (Solubility) 예측 범위
          </h4>
          <p className="text-xs text-gray-500 mb-3">
            log S 단위 / 임계선: 0(수용성), -3(비수용)
          </p>
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={solDistribution}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="logS"
                tick={{ fontSize: 11 }}
                label={{
                  value: "log S",
                  position: "insideBottomRight",
                  offset: -5,
                  fontSize: 11,
                }}
              />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip
                formatter={(value) => [`${value}`, "밀도"]}
                labelFormatter={(label) => `log S: ${label}`}
              />
              <ReferenceLine
                x={0}
                stroke="#22c55e"
                strokeDasharray="4 4"
                label={{ value: "수용성", fill: "#22c55e", fontSize: 10 }}
              />
              <ReferenceLine
                x={-3}
                stroke="#ef4444"
                strokeDasharray="4 4"
                label={{ value: "비수용", fill: "#ef4444", fontSize: 10 }}
              />
              <Area
                type="monotone"
                dataKey="density"
                stroke="#0ea5e9"
                fill="#bae6fd"
                fillOpacity={0.6}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
